import { VerifyUserEncrypted } from './../../models/verify.encrypted.model';
import { UserLoggedModel } from './../../models/user.logged.model';
import { RestEndpoint } from './../../../constants/rest-endpoint.constants';
import { LoginEncrypted } from './../../models/encrypt.base';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ResetModel } from '../../models/reset.model';
import { ForgotModel } from '../../models/forgot.model';
import { LoginUser } from '../../models/login.user.model';
import { LocalStorageService, SessionStorageService } from 'angular-web-storage';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { SignupEncrypt } from '../../models/signup.encrypt.base';
import { v4 as uuidv4 } from 'uuid';
import { LocalStorageKeys } from '../../utils/local.storage.keys';
import { UserIdentifierEnum } from '../../models/user-identifier.enum';
import { LanguagesEnum } from '../../models/languages.enum';
import { TranslateService } from '@ngx-translate/core';
import { IUserAddress } from '../../models/IUserAddress';

@Injectable()
export class AccountService {
    public static RSA_KEY: string;

   /**
    * Stores the user to be resetted
    */
    public userEmail: string;

    private readonly registerAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public getUserLogger$: Observable<boolean> = this.registerAccess.asObservable();

    constructor(
        private readonly storage: LocalStorageService,
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly sessionStorage: SessionStorageService,
        private readonly translate: TranslateService
    ) {
    }

    public getUsers(): Observable<Array<UserLoggedModel>> {
        return this.http.get(RestEndpoint.getAllUsers, {})
            .pipe(
                map((data: Array<UserLoggedModel>) => {
                    return data;
                }),
                catchError((err) => {
                    throw(err);
                })
        );
    }

    public getRsaPublicKey(): Observable<boolean | void> {
        if (AccountService.RSA_KEY)
        {
            return of ();
        }

        return this.http.get(RestEndpoint.publicRsa, {})
            .pipe(
                map((data: any) => {
                    if (!data.value) {
                        throw new Error('Problems to retrieve RSA Key.');
                    }
                    AccountService.RSA_KEY = data.value;
                    return of ();
                }),
                map(() => true),
                catchError((err) => {
                    throw(err);
                })
        );
    }

    /**
     * Get name and password inserted on login and compare with data. If it's match, return true to do login.
     * @param name
     * @param password
     */
    public loginUser(name: string, password: string, code: string): Observable<any> {
        return this.http.post<any>(RestEndpoint.authenticateUser, new LoginEncrypted(name, password, name, code, AccountService.RSA_KEY))
            .pipe(
                map((userData: UserLoggedModel) => {
                    if (!userData)
                    {
                        throw new HttpErrorResponse({status: 405, statusText: 'Impossible to retrieve user data.'});
                    }
                    userData.name = `${userData.firstName} ${userData.lastName}`;
                    this.storage.set(LocalStorageKeys.USER_LOGGED_KEY, userData);
                    this.registerAccess.next(true);
                }),
                catchError((err) => {
                    throw err;

                })
            );
    }

    public createUser(email: string, password: string, firstName: string, lastName: string, identifier: string, documentType: UserIdentifierEnum): Observable<any> {
        return this.http.post<any>(RestEndpoint.createUser, new SignupEncrypt(email, password, email, firstName, lastName, identifier, AccountService.RSA_KEY, documentType))
            .pipe(
                catchError((err) => {
                    throw err;

                })
            );
    }

    /**
     * Check email on Forgot Password page. If don't match, don't let go to Reset Password Page
     * @param user
     */
    public sendCode(user: ForgotModel): Observable<void> {
        this.userEmail = user.email;
        return this.http.post<void>(RestEndpoint.lostpassword, user)
            .pipe(
                catchError((err) => {
                    throw err;
                })
            );
    }

    /**
     * Destroy the session
     */
    public destroySession (): Observable<void>
    {
        this.storage.set(LocalStorageKeys.USER_LOGGED_KEY, undefined);
        this.registerAccess.next(false);
        return this.http.delete<void>( RestEndpoint.destroySession );
    }

    /**
     * Compare the email, verification code and new password to reset the password on Reset Password Page
     * @param reset
     */
    public resetPassword(reset: ResetModel): Observable<void> {
        return this.http.post<void>(RestEndpoint.changelostpassword, new VerifyUserEncrypted(reset, AccountService.RSA_KEY))
        .pipe(
            catchError((err) => {
                throw err;
            })
        );
    }

    public getUserDetails(): Observable<LoginUser> {
        const user = this.storage.get(LocalStorageKeys.USER_LOGGED_KEY);
        if (user) {
            return of(user);
        } else {
            return of(undefined);
        }
    }

    public logout(): void {
        this.destroySession().subscribe();
    }

    /**
     * Check if the user is authenticated
     */
    public isAuthenticated (menu?: boolean): Observable<UserLoggedModel>
    {
        return this.internalIsAuthenticated(menu);

    }

    private internalIsAuthenticated(menu?: boolean): Observable<any>
    {
        return this.http.post<boolean>( RestEndpoint.isAuthenticated, { })
        .pipe(
            map((authenticated: any) => {
                if ( !authenticated || !authenticated.isAuthenticated)
                {
                    if (!menu) {
                        this.destroySession().subscribe();
                        void this.router.navigate(['/']);

                    }
                    return;
                }
                if(authenticated.user) {
                    authenticated.user.name = `${authenticated.user.firstName} ${authenticated.user.lastName}`;

                    this.storage.set(LocalStorageKeys.USER_LOGGED_KEY, authenticated.user);
                }
                return of (this.storage.get(LocalStorageKeys.USER_LOGGED_KEY));
            })
        );
    }

    public getUserLanguage(): LanguagesEnum {
        const language: string = this.sessionStorage.get(LocalStorageKeys.LANGUAGE_LIST);
        if (!language) {
            const browserLang: string = this.translate.getBrowserLang();
            if (browserLang.match(/pt/)) {
                return LanguagesEnum.PORTUGUESE;
            } else {
                return LanguagesEnum.ENGLISH;
            }
        }
        return language as LanguagesEnum;
    }

    public changeBu(buId: string): Observable<any> {
        return this.http.post<any>(RestEndpoint.changeBu, {buId})
            .pipe(
                map((userData: any) => {
                    if (!userData)
                    {
                        throw new HttpErrorResponse({status: 405, statusText: 'Impossible to change BU.'});
                    }
                }),
                catchError((err) => {
                    throw err;

                })
            );
    }

    public sendNotification(data: {
        name: string,
        contact: string,
        observation: string,
        emailFrom: string,
        emailTo: string,
        product: string,
        period: string,
        storeId: string,
        subject: string
    }): Observable<any> {
        return this.http.post<any>(RestEndpoint.sendContactEmail, data)
            .pipe(
                map((userData: any) => {
                    if (!userData)
                    {
                        throw new HttpErrorResponse({status: 405, statusText: 'Impossible to send notification.'});
                    }
                }),
                catchError((err) => {
                    throw err;

                })
            );
    }


    /**
     * Return if the user is authenticated, but don't destroy the session if he isn't
     */
    public verifyAuthentication(): Observable<boolean> {
        return this.http.post<boolean>( RestEndpoint.isAuthenticated, { })
            .pipe(
                map((authenticated: any) => {
                    if ( !authenticated || !authenticated.isAuthenticated)
                    {
                        return false;
                    } else {
                        return true;
                    }
                })
            );
    }

    public verifyDocument(cpf: string, cnpj: string): Observable<any> {
        return this.http.get<any>(RestEndpoint.verifyDocument, {
            params: {
                'cpf': cpf,
                'cnpj': cnpj
            }
        }).pipe(
            catchError((err) => { throw err; })
        );
    }

    public changeNewsLetter(receivesNewletter: boolean, email: string): Observable<boolean> {
        return this.http.post<boolean>(RestEndpoint.changeNewsLetter, {receivesNewletter, email})
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError((err) => {
                    throw(err);
                })
            );
    }

    public readLocation(): Observable<Array<IUserAddress>> {
        return this.http.get(RestEndpoint.readLocation, {
        })
            .pipe(
                map((data: Array<IUserAddress>) => {
                    return data;
                }),
                catchError((err) => {
                    throw(err);
                })
        );
    }

    public createAddress(data: {
        id: string,
        street_address: string,
        city: string,
        state: string,
        country: string,
        postal_code: string,
        isDefault: boolean
    }): Observable<any> {
        data.id = uuidv4();
        return this.http.post<any>(RestEndpoint.createUserAddress, data)
            .pipe(
                map((userData: any) => {
                    if (!userData)
                    {
                        throw new HttpErrorResponse({status: 405, statusText: 'Impossible to send notification.'});
                    }
                }),
                catchError((err) => {
                    throw err;

                })
            );
    }
}
